/**
 * AddOnsManager - Manage event add-ons like lounge access, special meals, etc.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Plus, Edit2, Trash2, Package, DollarSign, Users, Tag,
  Loader2, Coffee, Sofa, Star, ShoppingBag, Ticket
} from 'lucide-react';
import api from '@/lib/api';

const ADDON_CATEGORIES = [
  { value: 'general', label: 'General', icon: Package },
  { value: 'lounge', label: 'Lounge Access', icon: Sofa },
  { value: 'meal', label: 'Special Meal', icon: Coffee },
  { value: 'vip', label: 'VIP Access', icon: Star },
  { value: 'merchandise', label: 'Merchandise', icon: ShoppingBag },
  { value: 'transport', label: 'Transport', icon: Ticket },
];

const AddOnsManager = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [showDialog, setShowDialog] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    category: 'general',
    capacity: null,
    is_active: true,
    requires_approval: false
  });

  // Fetch add-ons
  const { data: addons = [], isLoading } = useQuery({
    queryKey: ['addons', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}/addons`).then(r => r.data),
  });

  // Create add-on
  const createMutation = useMutation({
    mutationFn: (data) => api.post(`/admin/events/${eventId}/addons`, data),
    onSuccess: () => {
      toast.success('Add-on created successfully');
      queryClient.invalidateQueries(['addons', eventId]);
      setShowDialog(false);
      resetForm();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create add-on')
  });

  // Update add-on
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.put(`/admin/events/${eventId}/addons/${id}`, data),
    onSuccess: () => {
      toast.success('Add-on updated successfully');
      queryClient.invalidateQueries(['addons', eventId]);
      setShowDialog(false);
      resetForm();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to update add-on')
  });

  // Delete add-on
  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/admin/events/${eventId}/addons/${id}`),
    onSuccess: () => {
      toast.success('Add-on deleted');
      queryClient.invalidateQueries(['addons', eventId]);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to delete add-on')
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: 0,
      category: 'general',
      capacity: null,
      is_active: true,
      requires_approval: false
    });
    setEditingAddon(null);
  };

  const handleEdit = (addon) => {
    setEditingAddon(addon);
    setFormData({
      name: addon.name,
      description: addon.description || '',
      price: addon.price,
      category: addon.category || 'general',
      capacity: addon.capacity,
      is_active: addon.is_active !== false,
      requires_approval: addon.requires_approval || false
    });
    setShowDialog(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      price: parseFloat(formData.price),
      capacity: formData.capacity ? parseInt(formData.capacity) : null
    };
    
    if (editingAddon) {
      updateMutation.mutate({ id: editingAddon.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const getCategoryIcon = (category) => {
    const cat = ADDON_CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : Package;
  };

  const totalRevenue = addons.reduce((sum, addon) => 
    sum + (addon.price * (addon.sold_count || 0)), 0
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Event Add-Ons</h2>
          <p className="text-slate-500">Manage purchasable extras like lounge access, special meals, etc.</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="create-addon-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Add-On
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-100 rounded-full">
                <Package className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Add-Ons</p>
                <p className="text-2xl font-bold">{addons.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-full">
                <Tag className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Active</p>
                <p className="text-2xl font-bold">{addons.filter(a => a.is_active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Sold</p>
                <p className="text-2xl font-bold">{addons.reduce((sum, a) => sum + (a.sold_count || 0), 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <DollarSign className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Revenue</p>
                <p className="text-2xl font-bold">${totalRevenue.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add-Ons List */}
      <Card>
        <CardHeader>
          <CardTitle>All Add-Ons</CardTitle>
        </CardHeader>
        <CardContent>
          {addons.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No add-ons created yet</p>
              <p className="text-sm">Create add-ons like lounge access, special meals, or merchandise</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Capacity</TableHead>
                  <TableHead>Sold</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {addons.map((addon) => {
                  const Icon = getCategoryIcon(addon.category);
                  return (
                    <TableRow key={addon.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-slate-400" />
                          <div>
                            <p className="font-medium">{addon.name}</p>
                            {addon.description && (
                              <p className="text-xs text-slate-500 truncate max-w-xs">{addon.description}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {ADDON_CATEGORIES.find(c => c.value === addon.category)?.label || addon.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">${addon.price?.toFixed(2)}</TableCell>
                      <TableCell>{addon.capacity || '∞'}</TableCell>
                      <TableCell>
                        <span className={addon.sold_count > 0 ? 'text-green-600 font-medium' : ''}>
                          {addon.sold_count || 0}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={addon.is_active ? 'default' : 'secondary'}>
                          {addon.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(addon)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              if (confirm('Are you sure you want to delete this add-on?')) {
                                deleteMutation.mutate(addon.id);
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAddon ? 'Edit Add-On' : 'Create Add-On'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., VIP Lounge Access"
                required
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what's included..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="pl-9"
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Capacity</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.capacity || ''}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value || null })}
                  placeholder="Unlimited"
                />
              </div>
            </div>

            <div>
              <Label>Category</Label>
              <Select
                value={formData.category}
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADDON_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      <div className="flex items-center gap-2">
                        <cat.icon className="h-4 w-4" />
                        {cat.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
                <Label>Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.requires_approval}
                  onCheckedChange={(v) => setFormData({ ...formData, requires_approval: v })}
                />
                <Label>Requires Approval</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending) && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {editingAddon ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddOnsManager;
